import React, { useEffect, useMemo, useState } from 'react';
import PickerPopover from './PickerPopover';
import { fetchCourseChapterTree } from '../../../api/adminApi';

/**
 * Picks the chapter a month is planned from, as a searchable tree.
 *
 * A course is not a flat list: it is subject → chapter → topic, and one level
 * deeper again on the JEE courses. A single `<select>` of every row put six
 * chapters named "1. Introduction" next to each other with nothing to tell them
 * apart, hid whatever fell past the endpoint's page cap, and offered
 * soft-deleted chapters as things to plan.
 *
 * Any node is selectable, because what a class covers is not always a chapter:
 * a Foundation session plans "Water a Precious Resource", a JEE session plans
 * "Lecture 02" under "Video lectures", and course 182's "Exam discussion" is a
 * root holding contents with no children at all. The count on each row is the
 * node's own contents plus everything below it — that is what selecting it
 * pulls in.
 *
 * Every Foundation chapter also carries fixed **material buckets** — `Live
 * Class`, `Exam`, `Exam (level 2)`, `Class Notes`, `Work Book` — beside its
 * numbered teaching topics, and the mode decides which one a class comes from:
 * a Live class is scheduled from `Live Class`, a Unit Test from `Exam`. Those
 * rows are badged, and passing a `mode` highlights the matching bucket so it is
 * not buried among the topics.
 */

/** Badge text per material bucket; null-bucket rows show nothing. */
const BUCKET_LABEL = {
  live: 'live',
  exam: 'exam',
  notes: 'notes',
  workbook: 'workbook',
  textbook: 'text',
  advanced: 'advanced',
};

/** The bucket a session of this mode is normally scheduled from. */
const MODE_BUCKET = {
  Live: 'live',
  Exam: 'exam',
  'Unit Test': 'exam',
};

/** Matching nodes and their ancestors, so a filtered result still reads as a tree. */
function filterTree(nodes, needle) {
  if (!needle) return nodes;
  const keep = (node) => {
    if ((node.name || '').toLowerCase().includes(needle)) return node;
    const children = (node.children || []).map(keep).filter(Boolean);
    return children.length > 0 ? { ...node, children } : null;
  };
  return (nodes || []).map(keep).filter(Boolean);
}

/** Ids of every node in `nodes` that has children — used to expand a search result. */
function branchIds(nodes, collected = new Set()) {
  (nodes || []).forEach((node) => {
    if ((node.children || []).length > 0) {
      collected.add(node.id);
      branchIds(node.children, collected);
    }
  });
  return collected;
}

/** The ancestors of `chapterId`, so a restored selection opens to itself. */
function ancestorIds(nodes, chapterId, trail = []) {
  for (const node of nodes || []) {
    if (node.id === chapterId) return trail;
    const found = ancestorIds(node.children, chapterId, [...trail, node.id]);
    if (found) return found;
  }
  return null;
}

const ChapterTreePicker = ({ courseId, selectedChapterId, mode, onSelect }) => {
  const [tree, setTree] = useState([]);
  const [totalChapters, setTotalChapters] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!courseId) {
      setTree([]);
      setTotalChapters(0);
      setError(null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await fetchCourseChapterTree(courseId);
        if (!active) return;
        setTree(data.tree || []);
        setTotalChapters(data.totalChapters || 0);
        // Subjects start open; anything deeper stays closed, or a JEE course
        // would drop 1,240 rows on screen at once.
        setExpandedIds(new Set((data.tree || []).map((node) => node.id)));
      } catch (loadError) {
        if (active) setError('Failed to load this course’s chapters.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [courseId]);

  const wantedBucket = MODE_BUCKET[mode] || null;

  const needle = search.trim().toLowerCase();
  const filtered = useMemo(() => filterTree(tree, needle), [tree, needle]);

  // Float the bucket this mode is scheduled from to the top of each chapter:
  // `Live Class` and `Exam` otherwise sort in among a chapter's numbered topics,
  // which is where they were being missed.
  const visibleTree = useMemo(() => {
    if (!wantedBucket) return filtered;
    const lift = (nodes) => [...(nodes || [])]
      .map((node) => ({ ...node, children: lift(node.children) }))
      .sort((left, right) => {
        const leftMatch = left.bucket === wantedBucket ? 0 : 1;
        const rightMatch = right.bucket === wantedBucket ? 0 : 1;
        return leftMatch - rightMatch;
      });
    return lift(filtered);
  }, [filtered, wantedBucket]);

  // A search result is useless collapsed — the match is usually a leaf.
  const effectiveExpanded = useMemo(
    () => (needle ? branchIds(visibleTree) : expandedIds),
    [needle, visibleTree, expandedIds]
  );

  const selectedNode = useMemo(() => {
    if (!selectedChapterId) return null;
    const find = (nodes) => {
      for (const node of nodes || []) {
        if (node.id === selectedChapterId) return node;
        const found = find(node.children);
        if (found) return found;
      }
      return null;
    };
    return find(tree);
  }, [tree, selectedChapterId]);

  const toggleExpanded = (chapterId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(chapterId)) next.delete(chapterId); else next.add(chapterId);
      return next;
    });
  };

  const choose = (node) => {
    onSelect(node);
    setOpen(false);
    setSearch('');
    // Reopening should show the chosen node in place rather than collapsed.
    const trail = ancestorIds(tree, node.id) || [];
    setExpandedIds((current) => new Set([...current, ...trail]));
  };

  const renderNode = (node) => {
    const children = node.children || [];
    const isExpanded = effectiveExpanded.has(node.id);
    const isSelected = node.id === selectedChapterId;
    // The bucket this mode is normally scheduled from, so a Live class does not
    // have to be hunted for among the chapter's numbered topics.
    const isSuggested = Boolean(wantedBucket) && node.bucket === wantedBucket;

    return (
      <li key={node.id} className="sb-treeitem">
        <div className={`sb-treerow${isSelected ? ' selected' : ''}`} style={{ paddingLeft: node.depth * 16 }}>
          {children.length > 0 ? (
            <button
              type="button"
              className="sb-treetoggle"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
              onClick={() => toggleExpanded(node.id)}
            >
              {isExpanded ? '▾' : '▸'}
            </button>
          ) : (
            <span className="sb-treetoggle sb-treetoggle-empty" aria-hidden="true" />
          )}

          <button
            type="button"
            className={`sb-treename${isSuggested ? ' suggested' : ''}`}
            onClick={() => choose(node)}
          >
            <span>{node.name}</span>
            {node.bucket && (
              <span className={`sb-treebucket sb-treebucket-${node.bucket}`}>
                {BUCKET_LABEL[node.bucket]}
              </span>
            )}
            <span className="sb-treecount">{node.descendantContentCount}</span>
          </button>
        </div>

        {children.length > 0 && isExpanded && (
          <ul className="sb-treelist">{children.map(renderNode)}</ul>
        )}
      </li>
    );
  };

  const triggerValue = () => {
    if (loading) return 'Loading chapters…';
    if (error) return 'Chapters unavailable';
    if (!selectedNode) return '';
    return [...(selectedNode.path || []), selectedNode.name].join(' › ');
  };

  return (
    <>
      <PickerPopover
        label="Chapter"
        value={triggerValue()}
        placeholder={courseId ? 'Select a chapter…' : 'Pick a course first'}
        open={open}
        onOpenChange={setOpen}
        disabled={!courseId || loading || Boolean(error)}
      >
        <input
          type="search"
          className="sb-pickersearch"
          value={search}
          placeholder={`Search ${totalChapters} chapters…`}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search chapters"
        />

        {visibleTree.length === 0 ? (
          <span className="cal-empty">No chapter matches “{search}”.</span>
        ) : (
          <ul className="sb-treelist sb-treelist-root" role="tree">
            {visibleTree.map(renderNode)}
          </ul>
        )}
      </PickerPopover>

      {error && <div className="cal-error">{error}</div>}
    </>
  );
};

export default ChapterTreePicker;
